<template>
  <div class="space-y-6">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <h3 class="text-xl font-bold text-slate-800">Subscription</h3>
    </div>

    <div v-if="loading" class="py-16 text-center text-sm text-slate-500">
      Loading subscription details...
    </div>
    <div v-else-if="error" class="py-16 text-center text-sm font-medium text-red-600">
      {{ error }}
    </div>
    <div v-else class="flex flex-col gap-4 lg:flex-row">
      <section class="flex w-full flex-col gap-6 rounded-lg border border-slate-200 bg-white p-6 shadow-sm lg:max-w-xs">
        <div>
          <p class="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Subscription Status
          </p>
          <p class="text-xs text-slate-400">{{ subscription.plan }}</p>
        </div>

        <div class="space-y-4 text-sm text-slate-600">
          <div class="flex items-center gap-3">
            <span class="subscription-icon bg-blue-50 text-blue-500">
              <Icon icon-name="Subscription" />
            </span>
            <div>
              <p class="text-sm font-semibold text-slate-800">
                {{ subscription.plan }}
              </p>
              <p class="text-xs text-slate-500">
                {{ subscription.billingCycle }}
              </p>
            </div>
          </div>

          <div class="flex items-center gap-3">
            <span class="subscription-icon bg-green-50 text-green-600">
              <Icon icon-name="Connector" />
            </span>
            <div :class="['flex items-center gap-2 rounded-lg px-3 py-1', statusStyles.background]">
              <span :class="['status-dot', statusStyles.dot]"></span>
              <span :class="['text-sm font-semibold capitalize', statusStyles.text]">
                {{ statusLabel }}
              </span>
            </div>
          </div>

          <div class="flex items-center gap-3">
            <span class="subscription-icon bg-slate-100 text-slate-600">
              <Icon icon-name="CalendarIcon" class="h-4 w-4" />
            </span>
            <div class="flex flex-col">
              <span class="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Next Billing Date
              </span>
              <span class="text-sm text-slate-700">
                {{ subscription.nextBilling }}
              </span>
            </div>
          </div>
        </div>

        <div class="mt-auto flex items-center justify-between gap-3">
          <label 
            :class="['flex items-center gap-2 text-xs font-medium', canToggleStatus ? 'text-slate-500 cursor-pointer' : 'text-slate-300 cursor-not-allowed']"
            :title="canToggleStatus ? '' : 'Toggle only available for Active or Cancelled subscriptions'"
          >
            <span class="sr-only">Toggle subscription status</span>
            <span class="subscription-toggle">
              <input
                type="checkbox"
                :checked="isActive"
                :disabled="!canToggleStatus"
                class="subscription-toggle__input"
                @change="handleToggleChange"
              />
              <span :class="['subscription-toggle__track', !canToggleStatus && 'opacity-50']">
                <span class="subscription-toggle__thumb"></span>
              </span>
            </span>
            {{ isActive ? 'Set inactive' : 'Set active' }}
          </label>
        </div>
      </section>

      <section class="flex-1 p-6 ">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h4 class="text-base font-semibold text-slate-800">
              Billing Information
            </h4>
            <p class="text-xs uppercase tracking-wide text-slate-500">
              Upcoming
            </p>
          </div>
          <button type="button"
            class="rounded-lg bg-blue-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-600 focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:shadow-none"
            @click="openChangeModal">
            Change Subscription Id
          </button>
        </div>

        <div class="mt-4 overflow-hidden rounded-lg border border-slate-100 bg-white">
          <table class="min-w-full">
            <thead class="bg-slate-50">
              <tr class="text-left text-xs font-medium uppercase tracking-wide text-slate-800">
                <th class="px-4 py-3">Subscription Id</th>
                <th class="px-4 py-3">Date</th>
                <th class="px-4 py-3">Billing For</th>
                <th class="px-4 py-3">Billing Type</th>
              </tr>
            </thead>
            <tbody class="bg-white text-sm text-slate-700">
              <tr v-if="invoices.length === 0" class="text-center">
                <td colspan="6" class="px-4 py-6 text-sm text-slate-500">No upcoming invoices found.</td>
              </tr>
              <tr
                v-for="invoice in invoices"
                :key="invoice.id || invoice.number"
                class="hover:bg-slate-50 font-semibold text-slate-800 text-sm"
              >
                <td class="px-4 py-3 font-semibold text-slate-800 text-sm">
                  {{ invoice.id || invoice.number || "-" }}
                </td>
                <td class="px-4 py-3">
                  {{ invoice.date || "-" }}
                </td>
                <td class="px-4 py-3 capitalize">
                  {{ invoice.billingFor || "-" }}
                </td>
                <td class="px-4 py-3">
                  {{ invoice.billingType || "-" }}
                </td>
                <td class="px-4 py-3 text-right font-semibold text-black text-2xl align-middle">
                  {{ invoice.amount || "-" }}
                </td>
                <td class="px-4 py-3 text-right font-semibold align-middle">
                  <a
                    v-if="invoice.chargebeeUrl"
                    :href="invoice.chargebeeUrl"
                    target="_blank"
                    rel="noopener"
                    class="font-semibold text-blue-600 hover:text-blue-700"
                  >
                    View more
                  </a>
                  <span v-else class="text-blue-600 cursor-pointer"> View more</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <Modal
      :is-open="isChangeModalOpen"
      size="lg"
      class-name="core-modal"
      body-class-name="core-modal__body"
      @close="closeChangeModal"
    >
      <ChangeSubscriptionForm
        :value="changeForm"
        :errors="formErrors"
        :is-confirming="isChangeConfirming"
        :plan-options="planOptions"
        :currency-options="currencyOptions"
        :billing-cycle-options="billingCycleOptions"
        @save="handleFormSave"
        @confirm="handleConfirmChange"
        @cancel-confirm="handleCancelConfirm"
        @cancel="closeChangeModal"
        @close="closeChangeModal"
      />
    </Modal>

    <Modal
      :is-open="isStatusModalOpen"
      size="md"
      class-name="core-modal"
      body-class-name="core-modal__body"
      @close="closeStatusModal"
    >
      <StatusConfirmationModal
        :variant="statusModalVariant"
        @confirm="confirmStatusChange"
        @cancel="closeStatusModal"
        @close="closeStatusModal"
      />
    </Modal>
  </div>
</template>

<script>
import Icon from "../core/Icon.vue";
import Modal from "../core/Modal.vue";
import ChangeSubscriptionForm from "../Forms/ChangeSubscriptionForm.vue";
import StatusConfirmationModal from "../Forms/StatusConfirmationModal.vue";
import AnalyticsIntegration from "../../apis/AnalyticsIntegration";

export default {
  name: "SubscriptionTab",
  components: {
    Icon,
    Modal,
    ChangeSubscriptionForm,
    StatusConfirmationModal
  },
  props: {
    tenantId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      subscription: {
        id: "-",
        plan: "-",
        billingCycle: "-",
        status: "inactive",
        nextBilling: "-",
        billingType: "-",
        amount: "-",
        currency: ""
      },
      isActive: false,
      invoices: [],
      features: [],
      loading: false,
      error: null,
      isChangeModalOpen: false,
      isChangeConfirming: false,
      isStatusModalOpen: false,
      statusModalVariant: "activate",
      pendingChangePayload: null,
      changeForm: {
        subscriptionId: "",
        plan: null,
        currency: null,
        billingCycle: null
      },
      formErrors: {
        subscriptionId: "",
        plan: "",
        currency: "",
        billingCycle: ""
      },
      planLabelMap: {
        starter: "Starter Plan",
        basic: "Basic Plan",
        standard: "Standard Plan",
        professional: "Professional Plan",
        premium: "Premium Plan",
        old_basic: "Old Basic Plan",
        old_premium: "Old Premium Plan",
        other: "Other"
      },
      planOptions: [
        { label: "Starter Plan", value: "starter" },
        { label: "Basic Plan", value: "basic" },
        { label: "Standard Plan", value: "standard" },
        { label: "Professional Plan", value: "professional" },
        { label: "Premium Plan", value: "premium" },
        { label: "Old Basic Plan", value: "old_basic" },
        { label: "Old Premium Plan", value: "old_premium" },
        { label: "Other", value: "other" }
      ],
      currencyOptions: [
        { label: "USD", value: "USD" },
        { label: "AUD", value: "AUD" },
        { label: "NPR", value: "NPR" },
        { label: "EUR", value: "EUR" }
      ],
      billingCycleOptions: [
        { label: "Monthly", value: "monthly" },
        { label: "Quarterly", value: "quarterly" },
        { label: "Yearly", value: "yearly" },
        { label: "Every 3 Years", value: "every_3_years" }
      ]
    };
  },
  computed: {
    statusLabel() {
      const statusMap = {
        'in_trial': 'In Trial',
        'active': 'Active',
        'non_renewing': 'Non Renewing',
        'cancelled': 'Cancelled'
      };
      return statusMap[this.subscription.status] || this.subscription.status || 'Inactive';
    },
    statusStyles() {
      const status = this.subscription.status;
      if (status === 'active') {
        return { text: "text-green-600", dot: "bg-green-600", background: "bg-green-100" };
      } else if (status === 'in_trial') {
        return { text: "text-blue-600", dot: "bg-blue-600", background: "bg-blue-100" };
      } else if (status === 'non_renewing') {
        return { text: "text-orange-600", dot: "bg-orange-600", background: "bg-orange-100" };
      } else {
        return { text: "text-red-600", dot: "bg-red-600", background: "bg-red-100" };
      }
    },
    canToggleStatus() {
      const status = this.subscription.status;
      return status === 'active' || status === 'cancelled';
    }
  },
  watch: {
    isActive(value) {
      this.subscription.status = value ? "active" : "inactive";
    },
    tenantId: {
      immediate: true,
      handler(newVal) {
        if (!newVal) {
          return;
        }
        this.fetchSubscriptionData();
      }
    }
  },
  methods: {
    openChangeModal() {
      this.populateFormDefaults();
      this.isChangeConfirming = false;
      this.pendingChangePayload = null;
      this.isChangeModalOpen = true;
    },
    closeChangeModal() {
      this.isChangeModalOpen = false;
      this.isChangeConfirming = false;
      this.pendingChangePayload = null;
      this.resetFormErrors();
    },
    openStatusModal(action) {
      this.statusModalVariant = action === "activate" ? "activate" : "deactivate";
      this.isStatusModalOpen = true;
    },
    closeStatusModal() {
      this.isStatusModalOpen = false;
    },
    handleToggleChange() {
      // isActive represents the current state before toggle
      // If currently active, user wants to deactivate; if inactive, user wants to activate
      this.openStatusModal(this.isActive ? "deactivate" : "activate");
    },
    async confirmStatusChange() {
      try {
        await AnalyticsIntegration.toggleTenantSubscriptionStatus(this.tenantId);
        const newStatus = this.statusModalVariant === "deactivate" ? "inactive" : "active";
        this.isStatusModalOpen = false;
        this.$toast && this.$toast.success(`Subscription set to ${newStatus}`);
        await this.fetchSubscriptionData();
      } catch (error) {
        this.$toast && this.$toast.error("Failed to update subscription status");
        this.isStatusModalOpen = false;
      }
    },
    populateFormDefaults() {
      this.changeForm.subscriptionId = this.subscription.id && this.subscription.id !== "-" ? this.subscription.id : "";
      this.changeForm.plan = this.planOptions.find((option) => option.value === this.subscription.planType) || null;
      this.changeForm.currency = this.currencyOptions.find((option) => option.value === this.subscription.currency) || null;
      this.changeForm.billingCycle = this.billingCycleOptions.find((option) => option.value === this.subscription.billingCycleRaw) || null;
    },
    resetFormErrors() {
      this.formErrors = {
        subscriptionId: "",
        plan: "",
        currency: "",
        billingCycle: ""
      };
    },
    handleFormSave(payload) {
      const errors = {
        subscriptionId: payload.subscriptionId && payload.subscriptionId.trim() ? "" : "Subscription ID is required.",
        plan: payload.plan ? "" : "Plan is required.",
        currency: payload.currency ? "" : "Currency is required.",
        billingCycle: payload.billingCycle ? "" : "Billing cycle is required."
      };

      this.formErrors = errors;

      const hasErrors = Object.values(errors).some((message) => message);
      if (hasErrors) {
        return;
      }

      this.changeForm = {
        subscriptionId: payload.subscriptionId,
        plan: payload.plan,
        currency: payload.currency,
        billingCycle: payload.billingCycle
      };

      this.pendingChangePayload = payload;
      this.isChangeConfirming = true;
    },
    async handleConfirmChange() {
      if (!this.pendingChangePayload) {
        return;
      }

      this.isChangeConfirming = true;
      const payload = this.pendingChangePayload;
      const requestBody = {
        subscription_id: payload.subscriptionId,
        plan_code: this.buildPlanCode(payload.plan, payload.currency, payload.billingCycle),
        payment_currency: (payload.currency && payload.currency.value) || this.subscription.currency || null
      };

      try {
        await AnalyticsIntegration.changeTenantSubscriptionId(this.tenantId, requestBody);
        this.closeChangeModal();
        this.$toast && this.$toast.success("Subscription updated successfully");
        await this.fetchSubscriptionData();
      } catch (error) {
        this.$toast && this.$toast.error("Failed to update subscription");
        this.isChangeConfirming = false;
        this.pendingChangePayload = null;
      }
    },
    handleCancelConfirm() {
      this.isChangeConfirming = false;
      this.pendingChangePayload = null;
    },
    async fetchSubscriptionData() {
      this.loading = true;
      this.error = null;
      try {
        await this.$store.commit("TenantAnalyticsStore/SET_TENANT_BASIC_INFO", null);
        let payload = await this.$store.dispatch("TenantAnalyticsStore/fetchTenantBasicInfo", this.tenantId);

        payload = payload?.data ?? payload ?? {};
        this.applySubscriptionPayload(payload);
      } catch (error) {
        this.error = "Failed to load subscription details";
      } finally {
        this.loading = false;
      }
    },
    applySubscriptionPayload(payload = {}) {
      const subscription = payload.subscription || {};

      this.subscription = this.mapSubscription(subscription, payload);
      this.features = Array.isArray(payload.features) ? payload.features : [];
      this.invoices = this.mapInvoices(subscription);
      this.isActive = this.subscription.status === "active";
    },
    mapSubscription(subscription = {}, tenant = {}) {
      const planCode = subscription.plan_name || subscription.planName || tenant.plan_name || tenant.planName || "-";
      const planDetails = this.extractPlanDetails(planCode);
      const status = this.normalizeStatus(subscription.status || tenant.tenantSubscriptionStatus || tenant.status);
      const billingCycleRaw = (subscription.billing_period_unit || subscription.billingCycle || subscription.billing_period || planDetails.billingCycle || "").toString().toLowerCase();
      const nextBillingRaw = subscription.next_billing_at || subscription.next_billing_date || subscription.nextBillingDate || tenant.nextBillingDate || null;
      const amountRaw = subscription.next_billing_amount ?? subscription.output_amount ?? subscription.amount ?? tenant.nextAmount ?? null;
      const currency = (subscription.currency_code || subscription.currency || tenant.payment_currency || tenant.currency || planDetails.currency || "").toString().toUpperCase();

      return {
        id: subscription.id || subscription.subscription_id || tenant.subscription_id || tenant.subscriptionId || "-",
        plan: this.formatPlanName(planCode),
        planCode,
        planType: planDetails.planType,
        billingCycle: this.formatBillingCycle(billingCycleRaw),
        billingCycleRaw,
        status,
        nextBilling: nextBillingRaw ? this.formatDate(nextBillingRaw) : "-",
        billingType: subscription.billing_type || subscription.billingType || "Recurring",
        amount: this.formatAmount(amountRaw, currency),
        currency,
        currencyRaw: currency.toLowerCase()
      };
    },
    mapInvoices(subscription = {}) {
      const invoices = subscription?.invoices || subscription?.upcoming_invoices || [];

      if (!Array.isArray(invoices)) {
        return [];
      }

      if (invoices.length > 0) {
        return invoices.map((invoice) => {
          const amount = invoice.amount || invoice.total || invoice.amount_due;
          const currency =
            invoice.currency ||
            invoice.currency_code ||
            subscription.currency ||
            "";

          const subscriptionId =
            invoice.subscription_id ||
            invoice.subscriptionId ||
            invoice.subscription?.id ||
            subscription.id ||
            subscription.subscription_id ||
            subscription.subscriptionId ||
            null;
          const chargebeeUrl = subscriptionId
            ? `https://agentcis.chargebee.com/d/subscriptions/${subscriptionId}/edit`
            : null;

          return {
            id: invoice.id || invoice.invoice_id || invoice.number || null,
            number: invoice.number,
            date: invoice.date
              ? this.formatDate(invoice.date)
              : invoice.invoice_date
              ? this.formatDate(invoice.invoice_date)
              : "-",
            billingFor:
              invoice.description ||
              invoice.line_item_description ||
              invoice.plan_name ||
              subscription.plan_name ||
              "-",
            billingType: invoice.billing_type || subscription.billing_type || "Recurring",
            amount: amount != null ? this.formatAmount(amount, currency) : "-",
            url: invoice.download_url || invoice.invoice_url || null,
            chargebeeUrl
          };
        });
      }

      const fallbackInvoice = this.buildSubscriptionInvoice(subscription);
      return fallbackInvoice ? [fallbackInvoice] : [];
    },
    buildSubscriptionInvoice(subscription = {}) {
      const nextBillingAt =
        subscription.next_billing_at ||
        subscription.next_billing_date ||
        subscription.nextBillingDate;
      const nextAmount =
        subscription.next_billing_amount ||
        subscription.amount ||
        subscription.output_amount;
      const currency =
        subscription.currency ||
        subscription.currency_code ||
        subscription.currency_cde ||
        "";

      if (!nextBillingAt && nextAmount == null) {
        return null;
      }

      return {
        id: subscription.id || subscription.subscription_id || null,
        number: subscription.invoice_number || null,
        date: nextBillingAt ? this.formatDate(nextBillingAt) : "-",
        billingFor:
          subscription.plan_name ||
          subscription.planName ||
          subscription.description ||
          "-",
        billingType: subscription.billing_type || "Recurring",
        amount: nextAmount != null ? this.formatAmount(nextAmount, currency) : "-",
        url: subscription.invoice_url || null,
        chargebeeUrl: subscription.id
          ? `https://agentcis.chargebee.com/d/subscriptions/${subscription.id}/edit`
          : null
      };
    },
    formatAmount(amount, currency) {
      if (amount == null) {
        return "-";
      }

      const numericAmount = Number(amount);
      const formattedAmount = Number.isFinite(numericAmount)
        ? new Intl.NumberFormat(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(numericAmount)
        : amount;

      return `${currency ? currency + " " : ""}${formattedAmount}`.trim();
    },
    formatBillingCycle(cycle) {
      if (!cycle) {
        return "-";
      }

      return cycle
        .toString()
        .replace(/[_-]/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }

      const date = new Date(value.replace?.(" ", "T") || value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }

      return date.toLocaleString();
    },
    buildPlanCode(planOption, currencyOption, cycleOption) {
      const planType = (planOption && planOption.value) || this.subscription.planType;
      const currency = (currencyOption && currencyOption.value) || this.subscription.currencyRaw || this.subscription.currency;
      const cycle = (cycleOption && cycleOption.value) || this.subscription.billingCycleRaw;

      if (!planType) {
        return this.subscription.planCode;
      }

      const segments = [planType.toLowerCase()];

      if (currency) {
        segments.push(currency.toString().toLowerCase());
      }

      if (cycle) {
        segments.push(cycle.toString().toLowerCase());
      }

      return segments.join("-");
    },
    extractPlanDetails(planCode) {
      if (!planCode || planCode === "-") {
        return {
          planType: null,
          currency: null,
          billingCycle: ""
        };
      }

      const tokens = planCode.split(/[-_]/).filter(Boolean);
      const planType = tokens[0] ? tokens[0].toLowerCase() : null;
      const currency = tokens[1] ? tokens[1].toUpperCase() : null;
      const billingCycle = tokens.length > 2 ? tokens.slice(2).join("_").toLowerCase() : "";

      return {
        planType,
        currency,
        billingCycle
      };
    },
    formatPlanName(planCode) {
      if (!planCode || planCode === "-") {
        return "-";
      }

      const { planType, currency, billingCycle } = this.extractPlanDetails(planCode);
      const baseLabel = this.planLabelMap[planType] || this.formatTitleCase(planType || planCode);
      const suffix = [currency, billingCycle ? this.formatBillingCycle(billingCycle) : null]
        .filter(Boolean)
        .join(" · ");

      return suffix ? `${baseLabel} (${suffix})` : baseLabel;
    },
    formatTitleCase(value) {
      if (!value) {
        return "";
      }

      return value
        .toString()
        .replace(/[_-]/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },
    normalizeStatus(status) {
      if (typeof status === "number") {
        // Status codes: 2 = active, 4 = cancelled
        if (status === 2) return "active";
        if (status === 4) return "cancelled";
        return "cancelled";
      }

      if (typeof status === "string") {
        const normalized = status.toLowerCase().replace(/\s+/g, '_');
        // Map various status strings to normalized values
        const statusMap = {
          'in_trial': 'in_trial',
          'trial': 'in_trial',
          'active': 'active',
          'non_renewing': 'non_renewing',
          'non-renewing': 'non_renewing',
          'nonrenewing': 'non_renewing',
          'cancelled': 'cancelled',
          'canceled': 'cancelled',
          'inactive': 'cancelled'
        };
        return statusMap[normalized] || normalized;
      }

      return "cancelled";
    }
  },
  created() {
    this.fetchSubscriptionData();
  }
};
</script>

<style scoped>
.subscription-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
}

.status-dot {
  display: inline-block;
  height: 8px;
  width: 8px;
  border-radius: 9999px;
}

.subscription-toggle {
  position: relative;
  width: 42px;
  height: 24px;
  display: inline-block;
}

.subscription-toggle__input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.subscription-toggle__track {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  width: 42px;
  height: 24px;
  padding: 0 4px;
  border-radius: 9999px;
  background-color: #cbd5e1;
  transition: background-color 0.2s ease;
  box-sizing: border-box;
}

.subscription-toggle__thumb {
  width: 16px;
  height: 16px;
  border-radius: 9999px;
  background-color: #ffffff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.2);
  transition: transform 0.2s ease;
  transform: translateX(0);
}

.subscription-toggle__input:checked+.subscription-toggle__track {
  background-color: #0398dc;
}

.subscription-toggle__input:checked+.subscription-toggle__track .subscription-toggle__thumb {
  transform: translateX(18px);
}

.subscription-toggle__input:focus-visible+.subscription-toggle__track {
  box-shadow: 0 0 0 3px rgba(125, 211, 252, 0.45);
}

.subscription-toggle__input:disabled+.subscription-toggle__track {
  cursor: not-allowed;
  opacity: 0.5;
}
</style>